import React, { useEffect, useRef, useState } from 'react';
import axios from '../../../api/axiosInstance';
import { useReceivingPopupUi } from './receivingPopupMessages';
import { deliveryNoteCorrectionText } from './deliveryNoteCorrectionMessages';
import { deliveryNoteInventoryPreview, deliveryNoteInventoryValues, mergeDeliveryNoteUnitSource, formatUnitNumber } from '../../utils/deliveryNoteUnits';
import './DeliveryNoteCorrections.css';

const number = (value) => value === null || value === undefined || value === '' || !Number.isFinite(Number(value)) ? null : Number(value);
const tolerance = 1e-8;
const differs = (a, b) => Math.abs(a - b) > tolerance * Math.max(1, Math.abs(a), Math.abs(b));
const nameOf = (line) => line.item_name || line.description || line.item?.name || line.item_id;
const factorOf = (line, source = line) => deliveryNoteInventoryValues(line, source).factor;

export function receivedInventoryQuantities(line, source = line, documentType = 'standard') {
  const factor = factorOf(line, source);
  const documentQuantity = number(line.quantity_in_dn);
  let received = number(line.received_quantity);
  // New documents carry real receipts before they have a server-side snapshot.
  if (received === null && Array.isArray(line.po_receipts) && line.po_receipts.length) {
    received = line.po_receipts.reduce((total, receipt) => total + (number(receipt.quantity_received) || 0), 0);
  }
  const inventory = documentType === 'rectification' ? 0 : number(line.inventory_quantity_snapshot) ?? received ?? (documentQuantity === null ? null : documentQuantity / factor);
  return { documentQuantity, received: received === null ? null : received * factor, inventory: inventory === null ? null : inventory * factor };
}

export function correctionUnitSource(line, item) {
  if (line.correction_original_unit_level) line = { ...line,
    __persisted_unit_level: line.correction_original_unit_level,
    __persisted_unit_factor: line.correction_original_unit_factor,
    __persisted_unit_id: line.correction_original_unit_id,
    __persisted_selected_unit_name: line.correction_original_unit_name,
    __persisted_inventory_unit_id: line.inventory_unit_id || line.item_unit_id,
    __persisted_inventory_unit_name: line.inventory_unit_name || line.item_unit_name,
  };
  const snapshot = line.__persisted_unit_level || !Number(line.unit_factor) ? line : { ...line,
    __persisted_unit_level: line.unit_level || 'unit', __persisted_unit_factor: line.unit_factor,
    __persisted_unit_id: line.unit_id, __persisted_selected_unit_name: line.selected_unit_name || line.unit_name,
    __persisted_inventory_unit_id: line.inventory_unit_id || line.item_unit_id,
    __persisted_inventory_unit_name: line.inventory_unit_name || line.item_unit_name || line.unit_name,
  };
  return mergeDeliveryNoteUnitSource(item, snapshot);
}

export function deliveryNoteStockPreview(line, source, unitMap, languageId, documentType) {
  if (documentType === 'rectification' || documentType === 'supplier_return') return '';
  const values = receivedInventoryQuantities(line, source);
  return deliveryNoteInventoryPreview({ ...line, quantity_in_dn: values.inventory }, source, unitMap, languageId);
}

export function DeliveryNoteQuantityDetail({ line, source, documentType, children }) {
  const { languageId } = useReceivingPopupUi();
  const values = receivedInventoryQuantities(line, source, documentType);
  const show = values.documentQuantity !== null && ((values.received !== null && differs(values.received, values.documentQuantity)) || (values.inventory !== null && differs(values.inventory, values.documentQuantity)));
  return <div className="dnc-quantity">{children}{show && <span className="dnc-quantity__detail">{deliveryNoteCorrectionText(languageId, 'Received {received} · Inventory {inventory}', {
    received: values.received === null ? '—' : formatUnitNumber(values.received), inventory: values.inventory === null ? '—' : formatUnitNumber(values.inventory),
  })}</span>}</div>;
}

export function validateCorrectionLines(lines, languageId) {
  if (!lines.length || lines.some((line) => !(number(line.quantity_in_dn) > 0))) return deliveryNoteCorrectionText(languageId, 'Choose at least one positive correction quantity.');
  const totals = new Map();
  for (const line of lines) {
    if (!line.original_delivery_note_line_id) return deliveryNoteCorrectionText(languageId, 'Every correction line must be linked to an original delivery note line.');
    const key = String(line.original_delivery_note_line_id);
    const entry = totals.get(key) || { quantity: 0, line };
    entry.quantity += (number(line.quantity_in_dn) || 0) / factorOf(line);
    totals.set(key, entry);
  }
  for (const { quantity, line } of totals.values()) {
    const remaining = number(line.correction_available_quantity);
    if (remaining !== null && quantity - Math.abs(remaining) > tolerance) return deliveryNoteCorrectionText(languageId, 'The correction quantity for {item} exceeds the pending quantity ({quantity} {unit}).', {
      item: nameOf(line), quantity: formatUnitNumber(Math.abs(remaining) * factorOf(line)), unit: deliveryNoteInventoryValues(line).option?.label || '',
    });
  }
  return '';
}

function PendingQuantityInput({ quantity, disabled, label, onChange }) {
  const [draft, setDraft] = useState(null);

  const edit = (event) => {
    const text = event.target.value.replace('−', '-');
    if (!/^[+-]?\d*(?:[.,]\d*)?$/.test(text)) return;
    // Keep an unfinished sign or decimal separator visible while typing.
    if (text === '' || /\d/.test(text)) {
      const parsed = Number(text.replace(',', '.'));
      if (!Number.isFinite(parsed)) return;
      onChange(parsed);
    }
    setDraft(text);
  };

  return <input type="text" aria-label={label} disabled={disabled}
    value={disabled ? quantity : draft ?? quantity}
    onFocus={(event) => { if (quantity === 0) event.target.select(); }}
    onChange={edit} onBlur={() => setDraft(null)} />;
}

export function DeliveryNotePendingCorrection({ note, lines, sources, disabled, onChange, onLineChange }) {
  const { languageId } = useReceivingPopupUi();
  const t = (message, values) => deliveryNoteCorrectionText(languageId, message, values);
  if (note.document_type === 'rectification' || note.document_type === 'supplier_return' || note.correction_status === 'resolved') return null;
  const automatic = lines.some((line, index) => {
    const values = receivedInventoryQuantities(line, sources?.[index] || line);
    return values.received !== null && values.documentQuantity !== null && differs(values.received, values.documentQuantity);
  });
  const pending = automatic || ['pending', 'partial'].includes(note.correction_status);
  return <div className="dnc-pending">
    <label className="dnc-toggle"><input type="checkbox" checked={Boolean(note.correction_manual_pending || automatic)} disabled={disabled || automatic} onChange={(event) => onChange(event.target.checked)} />{t('Pending corrective delivery note')}</label>
    {automatic && <small className="dnc-help">{t('Pending automatically because received and documented quantities differ.')}</small>}
    {(note.correction_manual_pending || pending) && <details><summary>{t('Pending quantities')}</summary>
      {!automatic && !disabled && <small className="dnc-help">{t('Subtract the quantity received from the quantity on the delivery note, using the same unit. For example, 2 documented and 12 received gives −10.')}</small>}
      <div className="dnc-pending__lines">{lines.map((line, index) => {
        const source = sources?.[index] || line;
        const values = receivedInventoryQuantities(line, source);
        const factor = factorOf(line, source);
        const automaticQuantity = values.received === null || values.documentQuantity === null ? null : values.documentQuantity - values.received;
        const quantity = note.correction_status === 'partial' ? Number(line.correction_remaining_quantity || 0) * factor : automaticQuantity ?? (number(line.correction_pending_quantity) === null ? '' : Number(line.correction_pending_quantity) * factor);
        return <label key={line.id ?? line.temp_id ?? line._destinationKey ?? index}>
          <span>{nameOf(line)}</span>
          <PendingQuantityInput quantity={quantity} label={t('Quantity to correct for {item}', { item: nameOf(line) })}
            disabled={disabled || automaticQuantity !== null || note.correction_status === 'partial'}
            onChange={(value) => onLineChange(index, value / factor)} />
          <span>{deliveryNoteInventoryValues(line, source).option?.label}</span>
        </label>;
      })}</div>
    </details>}
  </div>;
}

export default function DeliveryNoteCorrectionControl({ note, businessUnitId, disabled, onSelect, onClear }) {
  const { languageId, themeId } = useReceivingPopupUi();
  const t = (message, values) => deliveryNoteCorrectionText(languageId, message, values);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ loading: false, notes: [], error: false });
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const closeRef = useRef(null);
  const dialogRef = useRef(null);
  const openerRef = useRef(null);
  const openPicker = () => { openerRef.current = document.activeElement; setOpen(true); };
  const close = () => { setOpen(false); setSelected(null); setError(''); setQuery(''); openerRef.current?.focus?.(); };
  closeRef.current = close;
  useEffect(() => { closeRef.current(); }, [note.id, note.supplier_id, businessUnitId]);
  useEffect(() => {
    if (!open) return undefined;
    const escape = (event) => {
      if (event.key === 'Escape') { event.stopPropagation(); closeRef.current(); }
      if (event.key === 'Tab' && dialogRef.current) {
        const controls = [...dialogRef.current.querySelectorAll('button:not(:disabled),input:not(:disabled)')];
        const first = controls[0]; const last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || !dialogRef.current.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', escape, true);
    return () => document.removeEventListener('keydown', escape, true);
  }, [open]);
  useEffect(() => {
    if (!open || !note.supplier_id) return undefined;
    let current = true;
    setState({ loading: true, notes: [], error: false });
    axios.get('/api/purchasing/receivings/delivery-notes/pending-corrections', {
      params: { supplier_id: note.supplier_id, ...(businessUnitId ? { business_unit_id: businessUnitId } : {}) },
      ...(businessUnitId ? { headers: { 'X-Pilcore-Business-Unit-Id': businessUnitId } } : {}),
    }).then((response) => {
      if (current) setState({ loading: false, notes: Array.isArray(response.data?.delivery_notes) ? response.data.delivery_notes : [], error: false });
    }).catch(() => { if (current) setState({ loading: false, notes: [], error: true }); });
    return () => { current = false; };
  }, [open, note.supplier_id, businessUnitId, attempt]);
  const isCorrection = note.document_type === 'rectification';
  const isReturn = note.document_type === 'supplier_return';
  const matchingNotes = state.notes.filter((original) => String(original.id) !== String(note.id) && [original.delivery_note_number, original.delivery_note_date, original.supplier?.name].some((value) => String(value || '').toLowerCase().includes(query.trim().toLowerCase())));
  const candidates = selected?.lines?.filter((line) => Math.abs(Number(line.correction_remaining_quantity)) > tolerance).map((line) => correctionUnitSource(line)) || [];
  const choose = (original) => {
    setSelected(original); setQuantities({}); setError('');
  };
  const link = () => {
    if (Object.values(quantities).some((value) => value !== '' && (number(value) === null || Number(value) < 0))) {
      setError(t('Choose at least one positive correction quantity.')); return;
    }
    const picked = candidates.filter((line) => Number(quantities[line.id]) > 0).map((line) => ({
      ...line, id: undefined, delivery_note_id: undefined, original_delivery_note_line_id: line.id,
      correction_available_quantity: Math.abs(Number(line.correction_remaining_quantity)),
      correction_direction: Number(line.correction_remaining_quantity) > 0 ? -1 : 1,
      quantity_in_dn: quantities[line.id], po_receipts: [], po_assignments: [], receipt_line_id: null, po_line_id: null, receiving_id: null, inventory_receipts_snapshot: [], received_quantity: null, quantity_received: 0,
      inventory_quantity_snapshot: 0, correction_pending_quantity: null, correction_remaining_quantity: null,
    }));
    const failure = validateCorrectionLines(picked, languageId);
    if (failure || !picked.length) { setError(failure || t('Choose at least one positive correction quantity.')); return; }
    onSelect(selected, picked); close();
  };
  return <>
    <select className="form-select dnc-type" aria-label={t('Delivery note type')} value={isReturn ? 'supplier_return' : isCorrection ? 'rectification' : 'standard'}
      disabled={disabled || !note.supplier_id || isReturn || Boolean(note.id && isCorrection)}
      onChange={(event) => event.target.value === 'rectification' ? openPicker() : onClear()}>
      <option value="standard">{t('Delivery note')}</option>
      <option value="rectification">{t('Corrective delivery note')}</option>
      {isReturn && <option value="supplier_return">{t('Return delivery note')}</option>}
    </select>
    {isCorrection && <button type="button" className="dnc-reference dnc-reference--button" disabled={disabled} onClick={openPicker}>{t('Original delivery note: {number}', { number: note.original_delivery_note_number || note.original_delivery_note_id })}</button>}
    {open && <div className="dnc-picker-overlay pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId} onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}><section ref={dialogRef} className="dnc-picker" role="dialog" aria-modal="true" aria-label={t('Choose an original delivery note')}>
      <header><h3>{t('Choose an original delivery note')}</h3><button type="button" onClick={close}>{t('Close')}</button></header>
      <small className="dnc-help">{t('Rectifications do not change inventory.')}</small>
      {!selected ? <>
        <input autoFocus className="dnc-picker__search" placeholder={t('Search delivery notes...')} aria-label={t('Search delivery notes...')} value={query} onChange={(event) => setQuery(event.target.value)} />
        {state.loading ? <p>{t('Loading...')}</p> : state.error ? <p role="alert">{t('Could not load pending delivery notes.')} <button type="button" onClick={() => setAttempt((value) => value + 1)}>{t('Retry')}</button></p> : <div className="dnc-picker__list">
          {matchingNotes.map((original) => <button type="button" className="dnc-picker__card" key={original.id} onClick={() => choose(original)}><strong>{original.delivery_note_number || `#${original.id}`}</strong><small>{String(original.delivery_note_date || '').slice(0, 10)}</small></button>)}
          {!matchingNotes.length && <p>{t('No pending delivery notes found.')}</p>}
        </div>}
      </> : <>
        <p>{t('Original delivery note: {number}', { number: selected.delivery_note_number || selected.id })}</p>
        {!candidates.length && <p>{t('Quantify the pending correction on the original delivery note first.')}</p>}
        <div className="dnc-picker__list">{candidates.map((line) => {
          const unit = deliveryNoteInventoryValues(line).option?.label || line.unit_name || '';
          const maximum = Math.abs(Number(line.correction_remaining_quantity)) * factorOf(line);
          return <label className="dnc-picker__line" key={line.id}><span>{nameOf(line)}<small>{t('Pending: {quantity} {unit}', { quantity: formatUnitNumber(maximum), unit })}</small></span><input type="number" min="0" max={maximum} step="0.00000001" aria-label={t('Quantity to correct for {item}', { item: nameOf(line) })} value={quantities[line.id] ?? ''} onChange={(event) => { setQuantities((previous) => ({ ...previous, [line.id]: event.target.value })); setError(''); }} /></label>;
        })}</div>
        {error && <p role="alert" className="dnc-picker__error">{error}</p>}
        <footer><button type="button" onClick={() => setSelected(null)}>{t('Back')}</button><button type="button" disabled={!candidates.length} onClick={link}>{t('Link selected lines')}</button></footer>
      </>}
    </section></div>}
  </>;
}
